import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import moment from 'moment';
import { DATA_TABLE_SOURCE_AUTOMATIC_ORDERS_DETAILS } from 'common/dataTableConstants';
import getNullEmptyValue from 'common/util/getNullEmptyValue';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrDataTableUI from 'client/components/SrDataTableUI';
import SrText from 'client/components/SrText';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import { SR_API_ACTION_AUTOMATIC_ORDERS_GET_ORDERS_SCREENSHOTS } from 'api/utilityActionTypes';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import useDispatch from 'client/hooks/useDispatch';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import ImageGallery from './ImageGallery';

const JsonDisplay = ({ data }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const formatValue = (value) => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return `"${value}"`;
    return String(value);
  };

  const renderJson = (obj) => {
    if (typeof obj !== 'object' || obj === null) {
      return <span>{formatValue(obj)}</span>;
    }

    const entries = Object.entries(obj);
    if (entries.length === 0) return '{}';

    return (
      <div style={{ paddingLeft: '1rem' }}>
        {entries.map(([key, value]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'flex-start' }}>
            <span style={{ fontWeight: 500 }}>{key}: </span>
            <div style={{ marginLeft: '0.5rem' }}>{renderJson(value)}</div>
          </div>
        ))}
      </div>
    );
  };

  const previewText = React.useMemo(() => {
    try {
      const str = JSON.stringify(data);
      return str.length > 50 ? str.substring(0, 47) + '...' : str;
    } catch (e) {
      return 'Invalid JSON';
    }
  }, [data]);

  return (
    <div style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'inherit',
          padding: '2px 4px',
          textAlign: 'left',
          font: 'inherit',
        }}
      >
        {isExpanded ? 'Collapse' : previewText}
      </button>
      {isExpanded && (
        <div
          style={{
            marginTop: '0.5rem',
            marginBottom: '0.5rem',
            padding: '0.5rem',
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
          }}
        >
          {renderJson(data)}
        </div>
      )}
    </div>
  );
};

// Helper to format datetime in user's timezone
const formatDateTime = (dateString) => {
  if (!dateString) return '--';
  return moment(dateString)
    .local() // Convert to local timezone
    .format('MMM D, YYYY h:mm A z'); // e.g., "Nov 11, 2024 3:30 PM EST"
};

const calculateExecutionTime = (startTime, endTime) => {
  if (!startTime || !endTime) return '--';

  const start = moment(startTime);
  const end = moment(endTime);
  const duration = moment.duration(end.diff(start));

  const seconds = Math.floor(duration.asSeconds());
  if (seconds < 60) {
    return `${seconds} sec`;
  }

  const minutes = Math.floor(duration.asMinutes());
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

function createAutomaticOrderRowsDetails({ rows, onViewScreenshots }) {
  return rows.map((row) => ({
    cells: [
      {
        dataTableFieldId: 'id',
        label: 'id',
        value: row.id || '--',
      },
      {
        dataTableFieldId: 'status',
        label: 'status',
        value: row.status || '--',
      },
      {
        dataTableFieldId: 'start_time',
        label: 'start_time',
        value: formatDateTime(row.start_time),
      },
      {
        dataTableFieldId: 'execution_time',
        label: 'execution_time',
        value: calculateExecutionTime(row.start_time, row.end_time),
      },
      {
        dataTableFieldId: 'response',
        label: 'response',
        content: row.response ? (
          <JsonDisplay
            data={typeof row.response === 'string' ? JSON.parse(row.response) : row.response}
          />
        ) : (
          '--'
        ),
      },
      {
        dataTableFieldId: 'confirmation_number',
        label: 'confirmation_number',
        value: row.confirmation_number || '--',
      },
      {
        label: 'View screenshots',
        content: row.order_id ? (
          <SrText
            font="greycliff"
            color="blue300"
            cursor="pointer"
            weight="800"
            fontSize="16px"
            lineHeight="50px"
            onClick={() => onViewScreenshots(row.order_id)}
          >
            View screenshots
          </SrText>
        ) : null,
      },
    ].map((cell) => ({
      ...cell,
      content: cell.content || getNullEmptyValue(cell.value, '--'),
    })),
  }));
}

const tableHeaders = [
  { dataTableFieldId: 'id', content: 'ID' },
  { dataTableFieldId: 'status', content: 'Status' },
  { dataTableFieldId: 'start_time', content: 'Start Time' },
  { dataTableFieldId: 'execution_time', content: 'Processing Time' },
  { dataTableFieldId: 'response', content: 'API Response' },
  { dataTableFieldId: 'confirmation_number', content: 'Confirmation Number' },
  { dataTableFieldId: 'actions', content: 'Actions' },
];

const ScreenshotsModal = ({ orderId, onClose }) => {
  const [screenshots, setScreenshots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchScreenshots = async () => {
      try {
        setIsLoading(true);
        const response = await dispatch(requestUtilityApi, {
          action: SR_API_ACTION_AUTOMATIC_ORDERS_GET_ORDERS_SCREENSHOTS,
          payload: {
            orderId,
          },
        });
        setScreenshots(response);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchScreenshots();
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SrDarkModal>
      <SrModalContent onCloseIconClick={onClose}>
        <div style={{ padding: '20px' }}>
          <div style={{ minHeight: '400px', minWidth: '600px' }}>
            {isLoading && <div>Loading screenshots...</div>}
            {error && <div>Error loading screenshots: {error}</div>}
            {!isLoading && !error && screenshots.length === 0 && (
              <div>No screenshots available</div>
            )}
            {!isLoading && !error && screenshots.length > 0 && (
              <ImageGallery images={screenshots} />
            )}
          </div>
        </div>
      </SrModalContent>
    </SrDarkModal>
  );
};

const UPAdminAutomaticOrdersDetails = ({ orderId }) => {
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const handleViewScreenshots = (orderId) => {
    setSelectedOrderId(orderId);
  };

  const handleCloseModal = () => {
    setSelectedOrderId(null);
  };

  return (
    <div>
      <Formik
        initialValues={{
          tableTheme: SR_TABLE_THEME_PARTNER_CLEAR,
        }}
      >
        {(formikBag) => (
          <SrDataTableConnected
            dataSource={DATA_TABLE_SOURCE_AUTOMATIC_ORDERS_DETAILS}
            initialViewSettings={{
              sort: {
                start_time: 'asc',
              },
              filter: {
                automatic_order_id: parseInt(orderId, 10),
              },
            }}
            count={20}
            itemsPropName="rows"
            resetOnUnmount
          >
            {({ rows, viewSettings, dataTableApiState, onInfiniteScrollWrapperRef }) => {
              const processedRows = createAutomaticOrderRowsDetails({
                rows,
                onViewScreenshots: handleViewScreenshots,
              });

              return (
                <div ref={onInfiniteScrollWrapperRef}>
                  <SrDataTableUI
                    theme={formikBag.values.tableTheme}
                    className="tableAccountDashboard"
                    dataTableViewSettings={viewSettings}
                    headers={tableHeaders}
                    rows={processedRows}
                    sticky
                  />
                  {dataTableApiState.inProgress && <div className="loading-ellipsis" />}
                  {selectedOrderId && (
                    <ScreenshotsModal orderId={selectedOrderId} onClose={handleCloseModal} />
                  )}
                </div>
              );
            }}
          </SrDataTableConnected>
        )}
      </Formik>
    </div>
  );
};

export default UPAdminAutomaticOrdersDetails;
